// Notification settings page.
//
// Standalone settings surface (same pattern as SecuritySettings) so it is
// identical for every role and reachable from the profile/avatar menu. It
// wires the real /users/notification-preferences/ endpoint:
//   - a master "Mute all notifications" switch
//   - per-category In-app and Email toggles
// Categories are ROLE-SCOPED: a user only sees the categories that can target
// one of their roles (see config/notificationCategories.js). Opt-out model:
// everything defaults to on; a missing preference row from the backend counts
// as enabled.
//
// Errors are surfaced via toast (consistent with the rest of the app).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { notificationPreferenceService } from "../../services/api";
import {
  getCategoriesForRoles,
  getCategoriesFromServer,
} from "../../config/notificationCategories";
import useTranslate from "../../hooks/useTranslate";

const ACCENT = "#2283FF";
const BORDER = "#E5E7EB";

// DESIGN_007 — server-side email policies act on the "Email" toggles without
// being visible anywhere. Each line is shown ONLY to the audience the policy
// actually applies to, so nobody is told about a rule that does not touch them.
//
//  1. Pitch digest. notify(..., digest_group='pitches') never sends its email
//     inline; dashboard/tasks.py send_pitch_email_digests bundles them into
//     ONE per-club email, throttled to PITCH_DIGEST_MIN_HOURS = 3. The
//     audience is club-side (the request creator plus the club admins).
//
// TWO lines used to sit here and were REMOVED, not forgotten. Both had become
// false promises, and both follow the same rule: the SENTENCE goes, the
// translation bundle row STAYS (see notification-settings.json — sixteen
// curated language values are never thrown away for a row that costs nothing
// unrendered; FE #315 had to restore one such row after it was deleted).
//
//  * Quiet hours — "Email is paused overnight, between 21:00 and 08:00
//    Europe/Zurich…". notifications/dispatch.py used to hold a gated
//    recipient's email back overnight and re-deliver it at 08:00. The gate was
//    REMOVED on 2026-08-03, settings and worker included, on Marin's word
//    ("Auch die Ruhezeiten fuer E-Mail koennen entfernt werden"). Email now
//    leaves when its event happens, at any hour, so the sentence promised a
//    pause the system no longer keeps. NotificationSettings.policies.test.js
//    guards its absence.
//  * Default-off requests — "New requests and pinned requests do not send email
//    until you have saved your preferences here", disclosing
//    EVENT_EMAIL_DEFAULT_OFF = {new_tender_posted, pinned_tender_active}. This
//    page shipped on 2026-07-29 (FE #263); hours later Marin reversed the rule
//    and BE #220 (2026-07-30) emptied the set to `frozenset()` on purpose —
//    "wir sind ein neues Tool, am Anfang duerfen diese Mails zur Aktivitaet als
//    Standard sein". The backend pins the emptiness
//    (users/tests/test_notification_switchability.py::
//    test_no_event_may_hide_its_email_default_from_the_settings_screen); if a
//    future event genuinely needs a default-off email, that test goes red FIRST
//    and this disclosure comes back with it.
const PITCH_DIGEST_ROLES = ["ClubAdmin", "Staff", "Coach"];

// Small controlled toggle switch, styled to match the role-switch toggle used
// elsewhere in the app (ProfileDropdown).
const Toggle = ({ checked, disabled, onChange, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`w-12 h-7 flex items-center rounded-full transition-colors duration-200 flex-shrink-0 ${
      checked ? "bg-[#2283FF]" : "bg-gray-300"
    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
        checked ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
);

const NotificationSettings = () => {
  // Current user's roles drive the OFFLINE FALLBACK category list only. The
  // authoritative list comes from the backend response (see load()), which is
  // the single source of truth for which categories a role may toggle.
  const roles = useSelector((state) => state.auth.roles || []);
  const { t } = useTranslate();
  const fallbackCategories = useMemo(
    () => getCategoriesForRoles(roles),
    [roles],
  );

  const [muted, setMuted] = useState(false);
  // Rendered categories: [{ key, label, description }]. Seeded from the role
  // fallback so the first paint is sensible, then replaced by the backend list.
  const [categories, setCategories] = useState(fallbackCategories);
  // prefs: { [category]: { in_app: bool, email: bool } }
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Build the default (all-on) preference map for a given category list.
  const buildDefaults = useCallback((list) => {
    const map = {};
    (list || []).forEach((c) => {
      map[c.key] = { in_app: true, email: true };
    });
    return map;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await notificationPreferenceService.getPreferences();
    if (res.success) {
      const data = res.data || {};
      setMuted(Boolean(data.notifications_muted));

      const serverPrefs = Array.isArray(data.preferences)
        ? data.preferences
        : [];

      // Backend is authoritative for WHICH categories to show. Fall back to the
      // role-based local list only if the server returned no categories.
      const serverCategories = getCategoriesFromServer(serverPrefs);
      const visibleCategories =
        serverCategories.length > 0 ? serverCategories : fallbackCategories;
      setCategories(visibleCategories);

      // Start from all-on defaults (opt-out model), then overlay any rows the
      // backend returned. A missing row stays enabled.
      const map = buildDefaults(visibleCategories);
      serverPrefs.forEach((row) => {
        if (row && row.category && map[row.category]) {
          map[row.category] = {
            in_app: row.in_app !== false,
            email: row.email !== false,
          };
        }
      });
      setPrefs(map);
    } else {
      // On failure, render the role-based fallback so the user is not stuck.
      setCategories(fallbackCategories);
      setPrefs(buildDefaults(fallbackCategories));
      setError(res.error || "Failed to load notification preferences.");
    }
    setLoading(false);
  }, [buildDefaults, fallbackCategories]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChannelChange = (categoryKey, channel, value) => {
    setPrefs((prev) => ({
      ...prev,
      [categoryKey]: {
        ...(prev[categoryKey] || { in_app: true, email: true }),
        [channel]: value,
      },
    }));
  };

  // Which policy line applies to this account. Read-only copy — nothing here
  // changes what is sent, it only discloses what already happens.
  const showPitchDigest = roles.some((r) => PITCH_DIGEST_ROLES.includes(r));
  const emailPolicies = [
    showPitchDigest &&
      "Pitch emails arrive bundled. Instead of one email per pitch you get a single summary, at most once every 3 hours. The in-app notification still arrives immediately.",
  ].filter(Boolean);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const payload = {
      notifications_muted: muted,
      preferences: categories.map((c) => ({
        category: c.key,
        in_app: prefs[c.key]?.in_app !== false,
        email: prefs[c.key]?.email !== false,
      })),
    };
    const res = await notificationPreferenceService.updatePreferences(payload);
    if (res.success) {
      toast.success("Notification preferences saved.");
    } else {
      toast.error(res.error || "Failed to save notification preferences.");
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[#0F172A]">Notifications</h1>
          <p className="text-sm text-[#475569] mt-1">
            Choose how you want to be notified. Turn off a channel for any
            category, or mute everything at once.
          </p>
        </header>

        <section
          className="bg-white rounded-xl shadow-sm border"
          style={{ borderColor: BORDER, borderRadius: 12 }}
        >
          {/* Master mute */}
          <div
            className="px-5 py-4 border-b flex items-center justify-between gap-4"
            style={{ borderColor: BORDER }}
          >
            <div>
              <h2 className="text-lg font-semibold text-[#0F172A]">
                Mute all notifications
              </h2>
              <p className="text-sm text-[#475569] mt-0.5">
                When on, you will not receive any in-app or email notifications.
                Your per-category settings below are kept and restored when you
                unmute.
              </p>
            </div>
            <Toggle
              checked={muted}
              disabled={loading || saving}
              onChange={setMuted}
              ariaLabel="Mute all notifications"
            />
          </div>

          {loading && (
            <div className="p-8 text-center text-[#94A3B8] text-sm">
              Loading preferences...
            </div>
          )}

          {!loading && error && (
            <div className="px-5 pt-3 text-center text-[#EF4444] text-sm">
              {error}
              <div className="mt-1">
                <button
                  type="button"
                  onClick={load}
                  className="text-[#2283FF] hover:underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && categories.length === 0 && (
            <div className="p-8 text-center text-[#94A3B8] text-sm">
              There are no notification categories for your account.
            </div>
          )}

          {!loading && categories.length > 0 && (
            <>
              {/* Column header */}
              <div
                className="px-5 py-2 flex items-center text-xs font-semibold text-[#94A3B8] border-b"
                style={{ borderColor: BORDER }}
              >
                <span className="flex-1">Category</span>
                <span className="w-20 text-center">In-app</span>
                <span className="w-20 text-center">Email</span>
              </div>

              <ul className="divide-y" style={{ borderColor: BORDER }}>
                {categories.map((c) => {
                  const value = prefs[c.key] || { in_app: true, email: true };
                  return (
                    <li
                      key={c.key}
                      className="px-5 py-4 flex items-center gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#0F172A]">
                          {c.label}
                        </div>
                        <div className="text-xs text-[#475569] mt-0.5">
                          {c.description}
                        </div>
                      </div>
                      <div className="w-20 flex justify-center">
                        <Toggle
                          checked={value.in_app !== false}
                          disabled={muted || saving}
                          onChange={(v) =>
                            handleChannelChange(c.key, "in_app", v)
                          }
                          ariaLabel={`${c.label} in-app notifications`}
                        />
                      </div>
                      <div className="w-20 flex justify-center">
                        <Toggle
                          checked={value.email !== false}
                          disabled={muted || saving}
                          onChange={(v) =>
                            handleChannelChange(c.key, "email", v)
                          }
                          ariaLabel={`${c.label} email notifications`}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div
                className="px-5 py-4 border-t flex items-center justify-between"
                style={{ borderColor: BORDER }}
              >
                <span className="text-xs text-[#94A3B8]">
                  {muted
                    ? "All notifications are currently muted."
                    : `${categories.length} categor${
                        categories.length === 1 ? "y" : "ies"
                      } shown for your account.`}
                </span>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="text-sm font-semibold text-white px-4 py-2 rounded-md disabled:opacity-50"
                  style={{ background: ACCENT }}
                >
                  {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </>
          )}
        </section>

        {/* DESIGN_007 — read-only disclosure of the email policies the toggles
            above do not expose. Nothing here is configurable; it exists so the
            "Email" switch does not read as a promise of instant mail. */}
        {!loading && emailPolicies.length > 0 && (
          <section
            aria-label={t("How email delivery works")}
            className="mt-4 rounded-xl border px-5 py-4"
            style={{ borderColor: BORDER, background: "#F8FAFC", borderRadius: 12 }}
          >
            <h2 className="text-sm font-semibold text-[#0F172A]">
              {t("How email delivery works")}
            </h2>
            <ul className="mt-2 list-disc pl-5 space-y-1.5 text-xs text-[#475569] leading-relaxed">
              {emailPolicies.map((line) => (
                <li key={line}>{t(line)}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
